import { useEffect, useState } from "react";
import { CircleMarker, MapContainer, Polyline, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../api/apiService";
import { useI18n } from "../i18n/I18nProvider";
import "../Map.css";

function coordinatePair(transfer, prefix) {
  const rawLat = transfer?.[`${prefix}_latitude`];
  const rawLon = transfer?.[`${prefix}_longitude`];
  // `Number(null)` and `Number("")` both equal zero.  A missing geocode must
  // never turn into a false marker at 0,0 or be sent to the routing provider.
  if (rawLat === null || rawLat === undefined || rawLat === "" || rawLon === null || rawLon === undefined || rawLon === "") return null;
  const lat = Number(rawLat);
  const lon = Number(rawLon);
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? [lat, lon] : null;
}

function suppliedCoordinatePair(point) {
  if (Array.isArray(point) && point.length === 2) {
    const [lat, lon] = point;
    return coordinatePair({ departure_latitude: lat, departure_longitude: lon }, "departure");
  }
  if (!point || typeof point !== "object") return null;
  return coordinatePair({
    departure_latitude: point.latitude ?? point.lat,
    departure_longitude: point.longitude ?? point.lon,
  }, "departure");
}

function MapViewport({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    if (points.length > 1) map.fitBounds(points, { padding: [26, 26], maxZoom: 13 });
  }, [map, points]);
  return null;
}

function openStreetMapUrl({ points, departure, arrival }) {
  if (points.length > 1) {
    const route = points.map(([lat, lon]) => `${lat},${lon}`).join(";");
    return `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${encodeURIComponent(route)}`;
  }
  const point = departure || arrival;
  return point ? `https://www.openstreetmap.org/?mlat=${encodeURIComponent(point[0])}&mlon=${encodeURIComponent(point[1])}#map=14/${point[0]}/${point[1]}` : null;
}

/** A map is displayed only when coordinates are saved on the transfer. */
export default function TransferMap({ transfer, waypoints }) {
  const { t } = useI18n();
  const departure = coordinatePair(transfer, "departure");
  const arrival = coordinatePair(transfer, "arrival");
  const supplied = Array.isArray(waypoints) ? waypoints.map(suppliedCoordinatePair).filter(Boolean) : [];
  const points = supplied.length ? supplied.filter((point, index, list) => index === 0 || point[0] !== list[index - 1][0] || point[1] !== list[index - 1][1]) : [departure, arrival].filter(Boolean);
  const routeDeparture = points[0] || departure;
  const routeArrival = points.at(-1) || arrival;
  const routeKey = points.map((point) => point.join(",")).join("/");
  const [roadRoute, setRoadRoute] = useState(null);
  const [routeError, setRouteError] = useState(false);
  useEffect(() => {
    let active = true;
    setRoadRoute(null); setRouteError(false);
    if (points.length < 2) return () => { active = false; };
    api.roadRoute(points).then((data) => {
      if (active && Array.isArray(data.geometry) && data.geometry.length > 1) setRoadRoute(data);
    }).catch(() => active && setRouteError(true));
    return () => { active = false; };
  // Stored coordinates are the sole route input.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);
  if (!points.length) return null;
  const externalMapUrl = openStreetMapUrl({ points, departure: routeDeparture, arrival: routeArrival });
  const mapPoints = roadRoute?.geometry || points;
  return <section className="transfer-map" aria-label={t("office.maps.label")}>
    <header><b>{t("office.maps.label")}</b>{externalMapUrl && <a href={externalMapUrl} target="_blank" rel="noopener noreferrer">{t("office.maps.open")} ↗</a>}</header>
    <MapContainer key={routeKey} center={points[0]} zoom={12} scrollWheelZoom={false} attributionControl={false}>
      <MapViewport points={mapPoints} />
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {routeDeparture && <CircleMarker center={routeDeparture} radius={8} pathOptions={{ color: "#1d4ed8", fillColor: "#2563eb", fillOpacity: 1 }} />}
      {routeArrival && routeArrival !== routeDeparture && <CircleMarker center={routeArrival} radius={8} pathOptions={{ color: "#b91c1c", fillColor: "#ef4444", fillOpacity: 1 }} />}
      {points.length > 1 && <Polyline positions={mapPoints} pathOptions={{ color: "#3156b8", weight: 4, dashArray: roadRoute ? undefined : "7 6" }} />}
    </MapContainer>
    {roadRoute ? <small>{t("office.maps.roadRoute", { distance: `${(roadRoute.distance_m / 1000).toFixed(1)} km`, duration: `${Math.round(roadRoute.duration_s / 60)} min` })}</small> : points.length > 1 ? <small>{routeError ? t("office.maps.routeFallback") : t("office.maps.routeLoading")}</small> : null}
    <small>© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors</small>
  </section>;
}
